import { BUNDLED_KERNEL_CANVAS_ARTIFACT } from "./builtins/bundledArtifact.js";
import {
    validateKernelCanvasArtifact,
} from "./kernelCanvasArtifact.js";
import {
    getTenantViewCloudState,
    loadTenantViewCloud,
} from "./tenantViewCloudStore.js";
import {
    DEFAULT_TENANT_VIEW_CAPABILITIES,
    getTenantView,
    importTenantViewBundle,
    validateTenantViewDefinition,
} from "./tenantViewsApi.js";
import { getCurrentUser } from "./userInfo.js";
import {
    assertTenantViewPortableArtifactShape,
    canonicalTenantViewArtifactJson,
    exportTenantViewArtifact,
    inventoryTenantViewArtifact,
    restoreTenantViewArtifact,
} from "./tenantViewPortable.js";

export const MAX_PORTABLE_ARTIFACT_BYTES = 6 * 1024 * 1024;
export const MAX_PORTABLE_ARTIFACT_OBJECTS = 21;
const MAX_INVENTORY_FINDINGS = 100;
const TEXT_ENCODER = new TextEncoder();
const FINDING_KIND = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BUNDLED_KERNELS_BY_ID = new Map(
    BUNDLED_KERNEL_CANVAS_ARTIFACT.objects
        .filter((object) => object.kind === "kernel")
        .map((object) => [object.id, object]),
);

function isObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function artifactLimits(capabilities = {}) {
    return {
        maxObjects: MAX_PORTABLE_ARTIFACT_OBJECTS,
        maxCanvases: 1,
        maxKernels: 20,
        maxArtifactBytes: MAX_PORTABLE_ARTIFACT_BYTES,
        ...(Number.isFinite(capabilities.maxKernelSteps)
            ? { maxStepsPerKernel: capabilities.maxKernelSteps }
            : {}),
        ...(Number.isFinite(capabilities.maxCanvasCells)
            ? { maxCellsPerCanvas: capabilities.maxCanvasCells }
            : {}),
        ...(Number.isFinite(capabilities.maxCanvasRows)
            ? { maxRowsPerCanvas: capabilities.maxCanvasRows }
            : {}),
        ...(Number.isFinite(capabilities.maxDefinitionBytes)
            ? { maxDefinitionBytes: capabilities.maxDefinitionBytes }
            : {}),
        ...(Number.isFinite(capabilities.maxDefinitionDepth)
            ? { maxJsonDepth: capabilities.maxDefinitionDepth }
            : {}),
        ...(Number.isFinite(capabilities.maxPortableArtifactBytes)
            ? { maxArtifactBytes: capabilities.maxPortableArtifactBytes }
            : {}),
    };
}

function validJsonPointer(pointer) {
    return typeof pointer === "string"
        && (pointer === "" || pointer.startsWith("/"))
        && !/~(?:[^01]|$)/.test(pointer);
}

function assertFindingInventory(artifact, name, objectIds) {
    const inventory = artifact[name];
    if (!isObject(inventory)) {
        throw new Error(`${name} must be an object`);
    }
    const keys = Object.keys(inventory).sort();
    if (keys.join(",") !== "findings,total,truncated") {
        throw new Error(`${name} must contain only findings, total, and truncated`);
    }
    if (!Array.isArray(inventory.findings)
        || inventory.findings.length > MAX_INVENTORY_FINDINGS) {
        throw new Error(`${name}.findings must contain at most ${MAX_INVENTORY_FINDINGS} entries`);
    }
    if (!Number.isSafeInteger(inventory.total)
        || inventory.total < inventory.findings.length) {
        throw new Error(`${name}.total must be an exact non-negative finding count`);
    }
    if (typeof inventory.truncated !== "boolean"
        || inventory.truncated !== (inventory.total > inventory.findings.length)) {
        throw new Error(`${name}.truncated must exactly report omitted findings`);
    }
    const seen = new Set();
    for (const [index, finding] of inventory.findings.entries()) {
        const path = `${name}.findings[${index}]`;
        if (!isObject(finding)
            || Object.keys(finding).sort().join(",") !== "json_pointer,kind,object_id") {
            throw new Error(`${path} must contain only object_id, kind, and json_pointer`);
        }
        if (!UUID_V4.test(finding.object_id || "") || !objectIds.has(finding.object_id)) {
            throw new Error(`${path}.object_id must name an object in the artifact`);
        }
        if (!FINDING_KIND.test(finding.kind || "")) {
            throw new Error(`${path}.kind is invalid`);
        }
        if (!validJsonPointer(finding.json_pointer)) {
            throw new Error(`${path}.json_pointer is invalid`);
        }
        const key = `${finding.object_id}\u0000${finding.kind}\u0000${finding.json_pointer}`;
        if (seen.has(key)) throw new Error(`${path} is duplicated`);
        seen.add(key);
    }
    return inventory;
}

function canonicalInventory(inventory) {
    return {
        findings: [...inventory.findings].sort((left, right) =>
            left.object_id.localeCompare(right.object_id)
            || left.json_pointer.localeCompare(right.json_pointer)
            || left.kind.localeCompare(right.kind)),
        total: inventory.total,
        truncated: inventory.truncated,
    };
}

export function serializePortableTenantViewArtifact(artifact) {
    const json = canonicalTenantViewArtifactJson(artifact);
    const bytes = TEXT_ENCODER.encode(json).byteLength;
    if (bytes > MAX_PORTABLE_ARTIFACT_BYTES) {
        throw new Error(
            `Backup is ${bytes} bytes; maximum is ${MAX_PORTABLE_ARTIFACT_BYTES}`,
        );
    }
    return json;
}

export function validatePortableTenantViewArtifact(artifact, { capabilities = {} } = {}) {
    const writableCapabilities = {
        ...DEFAULT_TENANT_VIEW_CAPABILITIES,
        ...(capabilities || {}),
    };
    const validation = validateKernelCanvasArtifact(artifact, {
        limits: artifactLimits(writableCapabilities),
    });
    if (!validation.ok) {
        const detail = validation.errors
            .map((error) => `${error.path}: ${error.message}`)
            .join("; ");
        throw new Error(`Invalid backup: ${detail}`);
    }
    serializePortableTenantViewArtifact(artifact);
    assertTenantViewPortableArtifactShape(artifact);
    for (const object of artifact.objects) {
        const bundled = BUNDLED_KERNELS_BY_ID.get(object.id);
        if (bundled
            && canonicalTenantViewArtifactJson(object)
                !== canonicalTenantViewArtifactJson(bundled)) {
            throw new Error(
                `Invalid backup object ${object.id}: bundled kernel definition does not match`,
            );
        }
        const definition = { ...object };
        delete definition.id;
        delete definition.kind;
        try {
            validateTenantViewDefinition(definition, {
                kind: object.kind,
                formatVersion: artifact.format_version,
                visibility: "draft",
                capabilities: writableCapabilities,
            });
        } catch (error) {
            throw new Error(
                `Invalid backup object ${object.id}: ${error?.message || error}`,
            );
        }
    }
    const objectIds = new Set(artifact.objects.map((object) => object.id));
    const identifierFindings = assertFindingInventory(
        artifact,
        "identifier_findings",
        objectIds,
    );
    const secretFindings = assertFindingInventory(
        artifact,
        "secret_findings",
        objectIds,
    );
    const expectedInventories = inventoryTenantViewArtifact(artifact);
    if (canonicalTenantViewArtifactJson(canonicalInventory(identifierFindings))
        !== canonicalTenantViewArtifactJson(
            canonicalInventory(expectedInventories.identifier_findings),
        )) {
        throw new Error(
            "This backup's identifier inventory does not match the current dashboard. "
            + "It may have been created by an older dashboard version or modified after "
            + "export; re-export it from the canvas and try again.",
        );
    }
    if (canonicalTenantViewArtifactJson(canonicalInventory(secretFindings))
        !== canonicalTenantViewArtifactJson(
            canonicalInventory(expectedInventories.secret_findings),
        )) {
        throw new Error("secret_findings do not exactly match the artifact definitions");
    }
    return {
        artifact,
        preview: {
            kernels: artifact.objects.filter((object) => object.kind === "kernel").length,
            canvases: artifact.objects.filter((object) => object.kind === "canvas").length,
            dependencies: artifact.dependencies.length,
            identifierFindings,
            secretFindings,
        },
    };
}

export function parsePortableTenantViewArtifact(text, options) {
    if (typeof text !== "string") throw new TypeError("backup file must contain text");
    const bytes = TEXT_ENCODER.encode(text).byteLength;
    if (bytes > MAX_PORTABLE_ARTIFACT_BYTES) {
        throw new Error(
            `Backup is ${bytes} bytes; maximum is ${MAX_PORTABLE_ARTIFACT_BYTES}`,
        );
    }
    let artifact;
    try {
        artifact = JSON.parse(text);
    } catch {
        throw new Error("Backup is not valid JSON");
    }
    return validatePortableTenantViewArtifact(artifact, options);
}

export async function inspectPortableTenantViewFile(file, { signal } = {}) {
    if (!file || typeof file.text !== "function") {
        throw new TypeError("Choose a JSON backup file");
    }
    if (Number.isFinite(file.size) && file.size > MAX_PORTABLE_ARTIFACT_BYTES) {
        throw new Error(
            `Backup is ${file.size} bytes; maximum is ${MAX_PORTABLE_ARTIFACT_BYTES}`,
        );
    }
    const user = await getCurrentUser();
    const tenantId = user?.currentTenant?.tenantId || null;
    if (!user?.userId || !tenantId) throw new Error("Sign in to restore a backup");
    const state = await readyCloudState(tenantId, { signal });
    return parsePortableTenantViewArtifact(await file.text(), {
        capabilities: state.capabilities,
    });
}

function safeFileStem(value) {
    const stem = String(value || "canvas")
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
    return stem || "canvas";
}

export function downloadPortableTenantViewArtifact(
    artifact,
    {
        canvasSlug = "canvas",
        documentImpl = globalThis.document,
        urlImpl = globalThis.URL,
        BlobImpl = globalThis.Blob,
    } = {},
) {
    const json = serializePortableTenantViewArtifact(artifact);
    if (!documentImpl?.createElement || !urlImpl?.createObjectURL || !BlobImpl) {
        throw new Error("Browser downloads are unavailable");
    }
    const blob = new BlobImpl([json], { type: "application/json" });
    const url = urlImpl.createObjectURL(blob);
    const anchor = documentImpl.createElement("a");
    anchor.href = url;
    anchor.download = `${safeFileStem(canvasSlug)}.origin-view.json`;
    anchor.hidden = true;
    documentImpl.body?.appendChild(anchor);
    try {
        anchor.click();
    } finally {
        anchor.remove();
        globalThis.setTimeout(() => urlImpl.revokeObjectURL(url), 0);
    }
    return { fileName: anchor.download, json };
}

async function readyCloudState(tenantId, { force = false, signal } = {}) {
    let state = getTenantViewCloudState(tenantId);
    if (force || state.status !== "ready") {
        state = await loadTenantViewCloud(tenantId, { force, signal });
    }
    if (state.status !== "ready") {
        throw state.error || new Error("Cloud view storage is unavailable");
    }
    return state;
}

export async function buildStoredCanvasPortableArtifact(
    canvas,
    { signal } = {},
) {
    if (!canvas?.tenantViewId) throw new Error("Only a stored canvas can be backed up");
    const user = await getCurrentUser();
    const tenantId = user?.currentTenant?.tenantId || null;
    if (!user?.userId || !tenantId) throw new Error("Sign in to back up stored canvases");
    const state = await readyCloudState(tenantId, { signal });
    const artifact = await exportTenantViewArtifact({
        metadata: state.rows.map((row) => row.metadata),
        getView: (id) => getTenantView(id, { signal }),
        viewerUserId: user.userId,
        canvasIds: [canvas.tenantViewId],
        bundledArtifact: BUNDLED_KERNEL_CANVAS_ARTIFACT,
        capabilities: state.capabilities,
    });
    return validatePortableTenantViewArtifact(artifact, {
        capabilities: state.capabilities,
    }).artifact;
}

export async function restorePortableTenantViewArtifact(
    artifact,
    {
        signal,
        cryptoImpl = globalThis.crypto,
        secretOverrides = [],
    } = {},
) {
    const user = await getCurrentUser();
    const tenantId = user?.currentTenant?.tenantId || null;
    if (!user?.userId || !tenantId) throw new Error("Sign in to restore a backup");
    const state = await readyCloudState(tenantId, { force: true, signal });
    validatePortableTenantViewArtifact(artifact, { capabilities: state.capabilities });
    const result = await restoreTenantViewArtifact(artifact, {
        capabilities: state.capabilities,
        cryptoImpl,
        secretOverrides,
        importBundle: (request) => importTenantViewBundle(request, {
            capabilities: state.capabilities,
            signal,
        }),
    });
    if (result.results.some((entry) =>
        ["created", "already_created"].includes(entry.status))) {
        await loadTenantViewCloud(tenantId, { force: true, signal });
    }
    return result;
}
